"use client"

import { useEffect, useState } from "react"
import { useTranslations } from "next-intl"
import Image from "next/image"
import type { StepProps, ListingCategory } from "@/types/listing"
import { cn } from "@/lib/utils"

type ProductType = "building_blocks" | "trading_cards" | "comics"

const PRODUCT_TYPE_TO_CATEGORY: Record<ProductType, ListingCategory> = {
  building_blocks: "lego",
  trading_cards: "trading_card",
  comics: "comic",
}

// Exhaustive inverse — a new category without a tile here is a compile error.
const CATEGORY_TO_PRODUCT_TYPE: Record<ListingCategory, ProductType> = {
  lego: "building_blocks",
  trading_card: "trading_cards",
  comic: "comics",
}

// Category iconography rule (ICONS.md § Category iconography): tile-sized
// category surfaces use the product cutout, never a generic glyph. Exhaustive
// Record — a new product type ships WITH its `public/categories/<id>.webp`
// (photo ids follow `CATEGORY_PHOTO_IDS` in browse-categories.ts).
const PRODUCT_TYPE_TO_PHOTO_ID: Record<ProductType, string> = {
  building_blocks: "lego",
  trading_cards: "trading-cards",
  comics: "comics",
}

export function ProductTypeStep({
  data,
  updateDataAction,
  onValidationChange,
}: StepProps) {
  const tCard = useTranslations("cardCreation.typeStep")
  const [selected, setSelected] = useState<ProductType>(
    () => CATEGORY_TO_PRODUCT_TYPE[data.listingCategory ?? "lego"],
  )

  useEffect(() => {
    onValidationChange?.(true)
  }, [onValidationChange])

  useEffect(() => {
    updateDataAction({ listingCategory: PRODUCT_TYPE_TO_CATEGORY[selected] })
  }, [selected])

  const handleSelect = (type: ProductType) => {
    setSelected(type)
  }

  const optionClass = (isSelected: boolean) =>
    cn(
      "flex items-center gap-4 rounded-xl p-4 text-left transition-colors",
      "bg-foreground/6 hover:bg-foreground/10",
      isSelected && "ring-2 ring-inset ring-foreground/90"
    )

  return (
    <div className="space-y-6 w-full min-w-0 max-w-full">
      <div className="grid gap-2 w-full min-w-0">
        <button
          type="button"
          onClick={() => handleSelect("building_blocks")}
          className={optionClass(selected === "building_blocks")}
        >
          <div className="category-stage relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-black/10 dark:border-white/10">
            {/* Cutout over glyph — ICONS.md § Category iconography. Literal
                template src per fitness-next-image-params; `unoptimized` as in
                shop-by-category.tsx. */}
            <Image
              src={`/categories/${PRODUCT_TYPE_TO_PHOTO_ID["building_blocks"]}.webp`}
              alt=""
              aria-hidden
              fill
              unoptimized
              className="object-contain p-1"
            />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-medium text-foreground">
              {tCard("buildingBlocks")}
            </span>
            <p className="mt-0.5 text-xs text-foreground/50">
              {tCard("buildingBlocksDesc")}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleSelect("trading_cards")}
          className={optionClass(selected === "trading_cards")}
        >
          <div className="category-stage relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-black/10 dark:border-white/10">
            {/* Cutout over glyph — ICONS.md § Category iconography. Literal
                template src per fitness-next-image-params; `unoptimized` as in
                shop-by-category.tsx. */}
            <Image
              src={`/categories/${PRODUCT_TYPE_TO_PHOTO_ID["trading_cards"]}.webp`}
              alt=""
              aria-hidden
              fill
              unoptimized
              className="object-contain p-1"
            />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-medium text-foreground">
              {tCard("tradingCard")}
            </span>
            <p className="mt-0.5 text-xs text-foreground/50">
              {tCard("tradingCardDesc")}
            </p>
          </div>
        </button>

        <button
          type="button"
          onClick={() => handleSelect("comics")}
          className={optionClass(selected === "comics")}
        >
          <div className="category-stage relative flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-black/10 dark:border-white/10">
            <Image
              src={`/categories/${PRODUCT_TYPE_TO_PHOTO_ID["comics"]}.webp`}
              alt=""
              aria-hidden
              fill
              unoptimized
              className="object-contain p-1"
            />
          </div>
          <div className="min-w-0 flex-1">
            <span className="font-medium text-foreground">
              {tCard("comics")}
            </span>
            <p className="mt-0.5 text-xs text-foreground/50">
              {tCard("comicsDesc")}
            </p>
          </div>
        </button>
      </div>
    </div>
  )
}
